import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';

import { hasImage } from '../fragments/blocks/ContentPageHeader.mappers';
import { mapImage } from '../fragments/Image.mappers';
import { PageMetadata } from '../utils/Page';
import { currentTheme } from '../utils/theme';
import { getDomainBaseUrl } from './domains';

type GenericPageContent = {
  title?: string;
  callToAction?: string;
  header?: ContentPageFragment['header'];
  metaDescription?: string;
  lead?: string;
  introText?: string;
  allowIndexing?: ContentPageFragment['allowIndexing'];
  date?: ContentPageFragment['date'];
  headerImage?: LocationPageFragment['headerImage'];
};

const buildFullUrl = (path: string) => `${getDomainBaseUrl()}${path}`;
function getMetaLinks(
  content: GenericPageContent,
  pageContext: SilverbackPageContext,
) {
  return [
    ...(pageContext.localizations
      ? // eslint-disable-next-line no-unsafe-optional-chaining
        pageContext.localizations?.map((localization) => {
          return {
            rel: 'alternate',
            href: buildFullUrl(localization.path),
            hrefLang: localization.locale,
          };
        })
      : []),
    {
      rel: 'canonical',
      href: buildFullUrl(
        pageContext.localizations?.find(
          (localization) => localization.locale === pageContext.locale,
        )!.path as string,
      ),
    },
  ];
}

export function buildMetadata(
  content: GenericPageContent,
  pageContext: SilverbackPageContext,
): PageMetadata {
  const image =
    content.header && hasImage(content.header?.media)
      ? mapImage({
          image: content.header.media.image,
          preset: 'header_landscape',
        })
      : content.headerImage && hasImage(content.headerImage)
      ? mapImage({
          image: content.headerImage.image,
          preset: 'header_landscape',
        })
      : undefined;

  return {
    // When rendering the SEO component, the Theme provider is not accessible,
    // so we cannot use useTheme() in there. Hence, we just forward the current
    // theme from here.
    currentTheme: currentTheme,
    title: content?.title || content?.callToAction,
    description:
      content.metaDescription ||
      content?.header?.lead ||
      content?.lead ||
      content?.introText ||
      undefined,
    lang: pageContext.locale as string,
    viewport:
      'width=device-width, initial-scale=1, maximum-scale=2, user-scalable=yes',
    generator: 'Gatsby',
    links: getMetaLinks(content, pageContext),
    locale: pageContext.locale as string,
    imageUrl: image?.src,
    imageAlt:
      // @ts-ignore
      image &&
      content.header &&
      content.header.media &&
      // @ts-ignore
      content.header.media.image?.alt
        ? // @ts-ignore
          content.header.media.image.alt
        : content.headerImage && content.headerImage.image?.alt
        ? content.headerImage.image?.alt
        : undefined,
    ogType: 'website',
    publishedTime: content.date?.start,
    noIndex: content.allowIndexing === false,
  };
}
